import { useRef, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import ButtonOne from "../../common/normal-button";
import { ApiError, fieldErrorsFrom, messageFrom, useMembershipClasses } from "./membersApi";
import { MEMBERS_ENDPOINT } from "../../config/urls";
import MemberForm from "./MemberForm";
import { EMPTY_MEMBER_VALUES, toPayload, validateMember } from "./membersUtils";

// Spec 2.4: only Mobile is required.
const SECTIONS = [
  {
    fields: ["membership_id", "salutation", "full_name", "membership_class", "mobile", "email", "dob"],
  },
];

const AddMemberModal = ({ api, onClose, onSaved, onOpenMember }) => {
  const [values, setValues] = useState(EMPTY_MEMBER_VALUES);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [existingMemberId, setExistingMemberId] = useState(null);
  const [saving, setSaving] = useState(false);
  const fieldRefs = useRef({});
  const classes = useMembershipClasses(api);

  const setValue = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  // Errors show under the field on blur ...
  const handleBlur = (key) => {
    setErrors((prev) => ({ ...prev, [key]: validateMember(values)[key] || "" }));
  };

  const focusFirst = (errorMap) => {
    const order = SECTIONS[0].fields;
    const first = order.find((key) => errorMap[key]);
    if (first) fieldRefs.current[first]?.focus();
  };

  // ... and again on Save, where the first invalid field also gets focus.
  const handleSave = async () => {
    const found = validateMember(values);
    setErrors(found);
    setFormError("");
    setExistingMemberId(null);
    if (Object.keys(found).length) {
      focusFirst(found);
      return;
    }
    setSaving(true);
    try {
      const member = await api.request(MEMBERS_ENDPOINT, { method: "POST", body: toPayload(values) });
      onSaved(member);
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        const fromServer = fieldErrorsFrom(error);
        if (fromServer.existing_member_id) setExistingMemberId(fromServer.existing_member_id);
        delete fromServer.existing_member_id;
        const known = Object.fromEntries(Object.entries(fromServer).filter(([key]) => key in values || key === "mobile"));
        setErrors(known);
        focusFirst(known);
        if (!Object.keys(known).length) setFormError(messageFrom(error));
      } else {
        setFormError(messageFrom(error));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="add-member-title">
      <DialogTitle id="add-member-title">Add member</DialogTitle>
      <DialogContent dividers>
        <MemberForm
          values={values}
          errors={errors}
          sections={SECTIONS}
          classes={classes}
          onChange={setValue}
          onBlurField={handleBlur}
          fieldRefs={fieldRefs}
        />
        {existingMemberId && (
          <Alert
            severity="warning"
            sx={{ mt: 1 }}
            action={
              <Button color="inherit" size="small" onClick={() => onOpenMember(Number(existingMemberId))}>
                View member
              </Button>
            }
          >
            A member with this number already exists.
          </Alert>
        )}
        {formError && (
          <Alert severity="error" role="alert" sx={{ mt: 1 }}>
            {formError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <ButtonOne variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </ButtonOne>
        <ButtonOne onClick={handleSave} disabled={saving || !values.mobile.trim()}>
          {saving ? "Adding…" : "Add member"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

export default AddMemberModal;
